import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { ClipboardList, Phone, Loader2, ArrowRight } from 'lucide-react';
import AddressForm from '@/components/AddressForm';

// Máscara de telefone no padrão brasileiro: (11) 99999-9999
const formatPhone = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const isValidPhone = (v) => v.replace(/\D/g, '').length >= 10;

export default function OnboardingSubscriptionData() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState({
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  });

  useEffect(() => {
    if (!user || user.user_type !== 'client') {
      navigate('/client/login');
      return;
    }
    checkStatus();
    prefillFromUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const prefillFromUser = () => {
    if (user?.phone) setPhone(formatPhone(user.phone));
    if (user?.address && typeof user.address === 'object') {
      setAddress((prev) => ({ ...prev, ...user.address }));
    }
  };

  const checkStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/client/onboarding-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Precisa ter veículo antes deste passo
      if (!res.data.has_vehicle) {
        navigate('/client/onboarding/vehicle', { replace: true });
        return;
      }
      // Se já concluiu a contratação (plano ativo ou pendente efetivado no gateway)
      if (res.data.has_active_plan || res.data.has_pending_plan) {
        navigate('/client/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Erro ao verificar status:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValidPhone(phone)) {
      toast.error('Informe um telefone válido no formato (11) 99999-9999');
      return;
    }

    if (!address.cep || !address.street || !address.number || !address.neighborhood || !address.city || !address.state) {
      toast.error('Preencha todos os campos obrigatórios do endereço');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/client/subscription-data`, {
        phone,
        address
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Dados salvos!');
      navigate('/client/onboarding/plan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar dados');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4 py-8">
      <Card className="w-full max-w-md p-8">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-primary">Etapa 2 de 3</span>
            <span className="text-xs text-muted-foreground">Dados para Assinatura</span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: '66%' }} />
          </div>
        </div>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-primary/10 p-3 rounded-xl">
            <ClipboardList className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Dados para Assinatura</h1>
            <p className="text-sm text-muted-foreground">Telefone e endereço para sua assinatura</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Telefone */}
          <div>
            <Label htmlFor="phone">Telefone *</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="phone"
                placeholder="(11) 99999-9999"
                className="pl-10"
                value={phone}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
                maxLength={16}
                required
                data-testid="subscription-phone-input"
              />
            </div>
          </div>

          {/* Endereço (componente reutilizado do cadastro de prestadores) */}
          <AddressForm value={address} onChange={setAddress} />

          <Button
            type="submit"
            className="w-full h-12"
            disabled={loading}
            data-testid="subscription-data-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Continuar <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </form>
      </Card>
    </div>
  );
}
